import { Component, OnInit, ElementRef, ViewChildren, QueryList, AfterViewInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormGroup, FormControl, Validators, FormsModule } from '@angular/forms';
import { CdkDragDrop, CdkDragEnd, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { debounceTime, distinctUntilChanged, merge, Subscription, forkJoin } from 'rxjs';
import JsBarcode from 'jsbarcode';

import { NotificationService } from './../../../../Services/notification.service';
import { StockService } from '../../../../Services/Stock/stock.service';
import { TaggingService } from '../../../../Services/Tagging/tagging.service';
import { CustomizeService } from '../../../../Services/Customize_settings/customize.service';
import { HttpParams } from '@angular/common/http';
import { FirmSelectionService } from '../../../../Services/firm-selection.service';
import { RouterLink } from '@angular/router';
import { ZplService } from '../../../../Services/zpl.service';
import { PrintManagerService } from '../../../../Services/print-manager.service';

@Component({
  selector: 'app-tag-labels',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, DragDropModule, RouterLink, FormsModule],
  templateUrl: './tag-labels.component.html',
})
export class TagLabelsComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly VISUAL_SCALE_FACTOR = 5;
  private readonly BARCODE_HEIGHT_TO_BAR_WIDTH_RATIO = 20;
  private readonly BARCODE_HEIGHT_TO_FONT_SIZE_RATIO = 4;

  private firmId: number = 1;
  tags: any[] = [];
  taggingsList: any[] = []; // Stores all available barcodes for range lookup
  filteredOptions: string[] = [];
  showDropdown = false;
  selectedOptionIndex: number = -1;
  settingsLoaded = false;
  isRangeMode = false; // Toggle UI between single and range
  showTemplateModal = false;
  templateName: string = '';
  showPrinterModal = false;
  savedPrinters: any[] = [];
  discoveredPrinters: any[] = [];
  printerFormGroup: FormGroup;
  isDiscovering = false;
  showAddPrinterForm = false;

  private firmSubscription!: Subscription;
  private tagCreatedSubscription!: Subscription;

  searchForm: FormGroup;
  rangeForm: FormGroup; // NEW
  barcodeSettingsForm: FormGroup;
  fieldSettingsForm: FormGroup;

  barcodeContainerStyle: any = {};
  private templateConfigs: any = {};
  public dbTemplates: any[] = [];

  labelStyle: any = {};
  tailStyle: any = {};
  fieldStyles: { [key: string]: any } = {};
  fieldPositions: { [key: string]: { x: number; y: number } } = {};

  fieldVisibility: { [key: string]: boolean } = {
    itemName: true, barcode: true, itemCode: true, totalWeight: true,
    unitPrice: true, itemCategory: true, itemSize: true, itemColor: false,
    itemStoneType: true,
  };
  fieldKeys: string[] = Object.keys(this.fieldVisibility);
  customLabels: { [key: string]: string } = {};
  defaultFieldLabels: { [key: string]: string } = {
    itemName: 'Product', barcode: 'Barcode', itemCode: 'Code',
    totalWeight: 'Weight', unitPrice: 'Price', itemCategory: 'Category',
    itemSize: 'Size', itemColor: 'Colour', itemStoneType: 'Stone',
  };

  templateDimensions: { [key: string]: { width: string, height: string } } = {
    '$100mmx15mm': { width: '100mm', height: '15mm' },
    '$25mmx25mm': { width: '25mm', height: '25mm' },
    '$50mmx25mm': { width: '50mm', height: '25mm' },
    '$40mmx20mm': { width: '40mm', height: '20mm' }
  };

  @ViewChildren('barcodeCanvas') barcodeCanvases!: QueryList<ElementRef<SVGElement>>;

  constructor(
    private stockService: StockService,
    private taggingService: TaggingService,
    private notificationService: NotificationService,
    private customizeService: CustomizeService,
    private firmSelectionService: FirmSelectionService,
    private zplService: ZplService,
    private printManagerService: PrintManagerService
  ) {
    this.searchForm = new FormGroup({ searchInput: new FormControl('') });

    // Initialize Range Form
    this.rangeForm = new FormGroup({
      fromBarcode: new FormControl('', Validators.required),
      toBarcode: new FormControl('', Validators.required)
    });

    this.printerFormGroup = new FormGroup({
      printerName: new FormControl('', Validators.required),
      printerLanguage: new FormControl('ZPL', Validators.required),
      printerConnection: new FormControl('USB', Validators.required),
      printerDpi: new FormControl(203, Validators.required),
      printerAddress: new FormControl(''),
      printerPort: new FormControl('9100'),
      isDefault: new FormControl(false)
    });

    this.barcodeSettingsForm = new FormGroup({
      topMargin: new FormControl(''),
      leftMargin: new FormControl(''),
      tailLength: new FormControl('35mm'),
      tailSide: new FormControl('left'),
      templateType: new FormControl(''),
      labelWidth: new FormControl('100mm'),
      labelHeight: new FormControl('15mm'),
      textAlign: new FormControl('left'),
      barcodeSize: new FormControl('20px'),
      deleteAll: new FormControl(false),
      generateBarcode: new FormControl(false),
      printerDpi: new FormControl('203'),
    });

    const fieldControls: { [key: string]: FormControl } = {};
    Object.keys(this.defaultFieldLabels).forEach((field) => {
      fieldControls[field + 'Label'] = new FormControl('');
      fieldControls[field + 'Size'] = new FormControl('10');
    });
    this.fieldSettingsForm = new FormGroup(fieldControls);
  }

  ngOnInit() {
    const activeFirm = this.firmSelectionService.getselectedFirmName();
    if (activeFirm && activeFirm.id) {
      this.firmId = activeFirm.id;
    }
    this.loadAllUserSettings();
    this.setupFormAutosave();

    this.searchForm.get('searchInput')?.valueChanges.subscribe((value) => {
      const input = (value || '').toLowerCase();
      this.filteredOptions = this.taggingsList
        ?.map((x: any) => x.barcode)
        ?.filter((barcode: string) => barcode.toLowerCase().includes(input)) || [];
    });

    this.firmSubscription = this.firmSelectionService.selectedFirmName$.subscribe(firm => {
      if (firm && firm.id) {
        const isDifferentFirm = this.firmId !== firm.id;
        this.firmId = firm.id;
        if (isDifferentFirm) {
          this.loadAllUserSettings();
        }
        this.getTaggingList(firm.id);
      }
    });

    this.tagCreatedSubscription = this.taggingService.tagCreated$.subscribe(() => {
      const firm = this.firmSelectionService.getselectedFirmName();
      if (firm && firm.id) {
        this.firmId = firm.id;
        this.getTaggingList(firm.id);
      }
    });
  }

  ngOnDestroy(): void {
    this.tagCreatedSubscription?.unsubscribe();
    this.firmSubscription?.unsubscribe();
  }

  ngAfterViewInit() {
    this.barcodeCanvases.changes.subscribe(() => {
      if (this.settingsLoaded) this.renderBarcodes();
    });
  }

  // --- RANGE LOGIC ---

  onRangeSearch() {
    if (this.rangeForm.invalid) {
      this.notificationService.showError('Please enter both barcodes', 'Error');
      return;
    }

    const from = this.rangeForm.value.fromBarcode;
    const to = this.rangeForm.value.toBarcode;

    // Get all barcodes from our pre-loaded list
    const allBarcodes = this.taggingsList.map(t => t.barcode).sort();

    const startIndex = allBarcodes.indexOf(from);
    const endIndex = allBarcodes.indexOf(to);

    if (startIndex === -1 || endIndex === -1) {
      this.notificationService.showError('One or both barcodes not found in stock', 'Error');
      return;
    }

    // Determine the actual range (handle if user enters barcodes in reverse order)
    const start = Math.min(startIndex, endIndex);
    const end = Math.max(startIndex, endIndex);
    const selectedRange = allBarcodes.slice(start, end + 1);

    this.notificationService.showSuccess(`Fetching ${selectedRange.length} items...`, 'Info');

    // Fetch details for all barcodes in range
    const requests = selectedRange.map(bc => this.stockService.getBarcodeDataWithBarcodeOrModelNo(bc));

    forkJoin(requests).subscribe({
      next: (responses: any[]) => {
        const allItems = responses.flatMap(res => res.data || []);
        this.processFetchedItems(allItems);
      },
      error: () => this.notificationService.showError('Failed to fetch range data', 'Error')
    });
  }

  processFetchedItems(items: any[]) {
    if (items.length === 0) return;

    this.tags = items.map((item: any) => ({
      itemName: item.std_item_name,
      barcode: item.std_barcode,
      itemCode: item.std_item_code,
      itemColor: item.std_color,
      itemCategory: item.category?.name,
      itemSize: item.std_item_size,
      itemStoneType: item.std_stone_type,
      totalWeight: item.std_total_wt,
      unitPrice: item.std_unit_price,
    }));

    // --- FIXED DYNAMIC TEMPLATE LOADING LOGIC ---
    const appliedTag = items[0].std_applied_tag;

    if (appliedTag) {
      // Set the template type EXACTLY to what the database says for this item
      this.barcodeSettingsForm.get('templateType')?.setValue(appliedTag, { emitEvent: false });
      this.onTemplateTypeChange(false);
    } else {
      // If the barcode has NO tag assigned, don't force one. 
      // Let the user select it manually.
      console.log("No applied tag found for this barcode.");
    }

    setTimeout(() => this.renderBarcodes(), 50);
  }

  toggleRangeMode() {
    this.isRangeMode = !this.isRangeMode;
    this.tags = []; // Clear current tags when switching modes
  }

  // --- REST OF CODE AS IS ---

  onSearch() {
    const barcode = this.searchForm.get('searchInput')?.value;
    if (barcode) this.getBarcodeData(barcode);
  }

  loadAllUserSettings() {
    // First, load the specific templates (Your existing logic)
    this.customizeService.getBarcodeTemplates(this.firmId).subscribe({
      next: (res: any) => {
        if (res.success && res.data) {
          this.dbTemplates = res.data;
          this.dbTemplates.forEach(t => {
            const mergedVisibility = { ...this.fieldVisibility };
            if (t.visibility) {
              this.fieldKeys.forEach(key => {
                if (t.visibility.hasOwnProperty(key)) mergedVisibility[key] = !!t.visibility[key];
              });
            }
            this.templateConfigs[t.slug] = {
              positions: (t.positions && !Array.isArray(t.positions)) ? t.positions : {},
              visibility: mergedVisibility,
              fieldSettings: t.font_sizes || {},
              settings: t.settings || {}
            };
          });
        }

        // --- NEW: Load General User Settings to restore positions/labels ---
        this.customizeService.getUserSettings(this.firmId).subscribe({
          next: (userRes: any) => {
            if (userRes.data) {
              const d = userRes.data;
              if (d.barcode_field_order) this.fieldKeys = d.barcode_field_order;
              if (d.barcode_field_positions) this.fieldPositions = (d.barcode_field_positions && !Array.isArray(d.barcode_field_positions)) ? d.barcode_field_positions : {};
              if (d.field_settings) {
                this.fieldSettingsForm.patchValue(d.field_settings, { emitEvent: false });
                this.updateCustomLabelsAndStyles(d.field_settings);
              }
            }
            this.settingsLoaded = true;
            // FIX: Only trigger change if a value ACTUALLY exists in the form
            const currentType = this.barcodeSettingsForm.get('templateType')?.value;
            if (currentType) {
              this.onTemplateTypeChange(false);
            }
          },
          error: () => {
            this.settingsLoaded = true;
            this.onTemplateTypeChange(false);
            this.applySettings();
          }
        });
      }
    });
  }

  saveAllUserSettings() {
    if (!this.settingsLoaded) return;

    const type = this.currentTemplateType;

    // Guard Clause: If no type is selected, do not proceed with saving
    if (!type) {
      alert("Save aborted: No template type selected.");
      return;
    }

    // getRawValue() is CRITICAL here to capture disabled fields
    const barcodeSettings = this.barcodeSettingsForm.getRawValue();
    const fieldSettings = this.fieldSettingsForm.getRawValue();

    const payload = {
      firm_id: this.firmId,
      name: this.getTemplateDisplayName(type),
      slug: type,
      settings: barcodeSettings,   // Margins, Tail, DPI, Width, Height
      positions: this.fieldPositions, // X, Y coordinates
      font_sizes: fieldSettings,     // Custom Labels and Font Sizes
      visibility: this.fieldVisibility // Show/Hide states
    };

    // Sync Local Cache
    this.templateConfigs[type] = {
      positions: payload.positions,
      visibility: payload.visibility,
      fieldSettings: payload.font_sizes,
      settings: payload.settings
    };

    this.customizeService.saveBarcodeTemplate(payload).subscribe({
      next: () => this.notificationService.showSuccess(`Setting saved for ${type}`, "success"),
      error: () => this.notificationService.showError('Autosave failed', 'Error')
    });

    const generalSettingsPayload = {
      customize_firm_id: this.firmId,
      barcode_field_order: this.fieldKeys,             // Saves the order of Product, Barcode, etc.
      barcode_field_positions: this.fieldPositions,     // Saves the Drag & Drop coordinates
      field_settings: fieldSettings,                   // Saves your Custom Labels and Font Sizes
      barcode_settings: {                              // Saves Visibility and other UI settings
        ...barcodeSettings,
        visibility: this.fieldVisibility
      }
    };

    this.customizeService.saveUserSettings(generalSettingsPayload).subscribe({
      next: () => this.notificationService.showSuccess(`Setting saved for ${type}`, "success"),
      error: () => console.error('General settings save failed')
    });
  }

  // Helper to get display name for the database "name" column
  getTemplateDisplayName(slug: string): string {

    const names: any = {
      '$100mmx15mm': '100mm X 15mm',
      '$25mmx25mm': '25mm X 25mm',
      '$50mmx25mm': '50mm X 25mm',
      '$40mmx20mm': '40mm X 20mm',
      '$custom': 'Custom Size'
    };
    return names[slug] || slug;
  }

  setupFormAutosave(): void {
    merge(this.barcodeSettingsForm.valueChanges, this.fieldSettingsForm.valueChanges)
      .pipe(
        debounceTime(1000),
        distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b))
      )
      .subscribe(() => {
        // Updates the visual box and labels only
        this.applySettings();
        const fieldSettings = this.fieldSettingsForm.value;
        this.updateCustomLabelsAndStyles(fieldSettings);

        // Removed saveAllUserSettings() from here
      });
  }

  onTemplateTypeChange(shouldSave: boolean = false) {
    const type = this.currentTemplateType; // This is what the user just selected

    const isStandard = type.startsWith('$');
    const baseType = isStandard ? type : '$custom';

    const config = this.templateConfigs[type] || this.initialTemplateConfigs[baseType];

    if (config) {
      if (config.settings) {
        // --- FIX: Create a copy and remove templateType so it doesn't overwrite our selection ---
        const settingsToPatch = { ...config.settings };
        delete settingsToPatch.templateType;

        this.barcodeSettingsForm.patchValue(settingsToPatch, { emitEvent: false });
      }

      this.fieldPositions = JSON.parse(JSON.stringify((config.positions && !Array.isArray(config.positions)) ? config.positions : {}));
      this.fieldVisibility = JSON.parse(JSON.stringify(config.visibility || {}));

      if (config.fieldSettings) {
        this.fieldSettingsForm.patchValue(config.fieldSettings, { emitEvent: false });
        this.updateCustomLabelsAndStyles(config.fieldSettings);
      }
    }

    // Handle disabling inputs for standard templates
    if (baseType === '$custom') {
      this.barcodeSettingsForm.get('labelWidth')?.enable({ emitEvent: false });
      this.barcodeSettingsForm.get('labelHeight')?.enable({ emitEvent: false });
    } else {
      this.barcodeSettingsForm.get('labelWidth')?.disable({ emitEvent: false });
      this.barcodeSettingsForm.get('labelHeight')?.disable({ emitEvent: false });
    }

    this.clampToSafeBoundary();
    this.applySettings();

    if (shouldSave) {
      this.saveAllUserSettings();
    }
  }

  private applyTemplateDefaultFontSizes(type: string) {
    const isLarge = (type === '$100mmx15mm' || type === '$custom' || type === '$50mmx25mm');
    const defaultSize = isLarge ? '7' : '5';
    const updatedSizes: any = {};
    this.fieldKeys.forEach(key => {
      updatedSizes[key + 'Size'] = (key === 'barcode') ? '5' : defaultSize;
    });
    this.fieldSettingsForm.patchValue(updatedSizes, { emitEvent: false });
    this.onSubmitFieldSettings();
  }

  private clampToSafeBoundary() {
    const settings = this.barcodeSettingsForm.getRawValue();
    const wMM = this.parseToMM(settings.labelWidth) || 40;
    const hMM = this.parseToMM(settings.labelHeight) || 20;
    const tailMM = settings.tailSide !== 'none' ? (this.parseToMM(settings.tailLength) || 0) : 0;

    // Use actual printable width
    const printableWMM = Math.max(10, wMM - tailMM);

    const maxW = printableWMM * this.VISUAL_SCALE_FACTOR;
    const maxH = hMM * this.VISUAL_SCALE_FACTOR;

    Object.keys(this.fieldPositions).forEach(key => {
      const pos = this.fieldPositions[key];

      // Ensure X is within bounds
      let newX = Math.max(2, pos.x);
      if (newX > maxW - 20) newX = Math.max(2, maxW - 30);

      // Ensure Y is within bounds
      let newY = Math.max(2, pos.y);
      if (newY > maxH - 10) newY = Math.max(2, maxH - 15);

      this.fieldPositions[key] = { x: newX, y: newY };
    });
  }

  private getClampedPosition(pos: { x: number, y: number }, maxW: number, maxH: number) {
    let newX = pos.x;
    let newY = pos.y;
    if (newX < 2) newX = 2;
    if (newY < 2) newY = 2;
    if (newX > maxW - 30) newX = Math.max(2, maxW - 40);
    if (newY > maxH - 12) newY = Math.max(2, maxH - 15);
    return { x: newX, y: newY };
  }

  applySettings() {
    const settings = this.barcodeSettingsForm.getRawValue();

    // Parse dimensions
    const labelWidthMM = this.parseToMM(settings.labelWidth) || 40;
    const labelHeightMM = this.parseToMM(settings.labelHeight) || 20;

    // Calculate Tail Length
    const tailLengthMM = settings.tailSide !== 'none' ? (this.parseToMM(settings.tailLength) || 0) : 0;

    // ACTUAL PRINTABLE WIDTH (Total Width - Tail)
    const printableWidthMM = Math.max(10, labelWidthMM - tailLengthMM);

    // Convert MM to Pixels for the Designer
    const labelWidthPx = printableWidthMM * this.VISUAL_SCALE_FACTOR;
    const labelHeightPx = labelHeightMM * this.VISUAL_SCALE_FACTOR;
    const tailWidthPx = tailLengthMM * this.VISUAL_SCALE_FACTOR;

    // Force fields to stay inside the newly sized box
    this.clampToSafeBoundary();

    // Container Style
    this.barcodeContainerStyle = {
      'margin-top': this.formatDimension(settings.topMargin, 'px') || '0px',
      'margin-left': this.formatDimension(settings.leftMargin, 'px') || '0px',
      'display': 'flex',
      'align-items': 'center',
      'justify-content': 'center',
      'gap': '0px'
    };

    // THE WHITE PRINTABLE BOX (Width = Printable Width now!)
    this.labelStyle = {
      width: `${labelWidthPx}px`,
      height: `${labelHeightPx}px`,
      position: 'relative',
      'background-color': 'white',
      'border': '1px solid #000',
      'overflow': 'hidden',
      'box-shadow': '0 4px 6px -1px rgb(0 0 0 / 0.1)'
    };

    // THE TAIL (Grey bar)
    if (settings.tailSide !== 'none' && tailWidthPx > 0) {
      this.tailStyle = {
        display: 'block',
        width: `${tailWidthPx}px`,
        height: '10px', // Standard thin tail height
        'background-color': '#d1d5db',
        'border': '1px solid #9ca3af',
        'border-right': settings.tailSide === 'left' ? 'none' : '1px solid #9ca3af',
        'border-left': settings.tailSide === 'right' ? 'none' : '1px solid #9ca3af',
        'z-index': '1'
      };
    } else {
      this.tailStyle = { display: 'none' };
    }

    setTimeout(() => this.renderBarcodes(), 50);
  }


  // Ensure drag updates and saves the row
  onDragEnded(event: CdkDragEnd, fieldKey: string) {
    const settings = this.barcodeSettingsForm.getRawValue();

    const wMM = this.parseToMM(settings.labelWidth) || 40;
    const tailMM = settings.tailSide !== 'none' ? (this.parseToMM(settings.tailLength) || 0) : 0;
    const printableWMM = Math.max(10, wMM - tailMM);

    const labelWidthPx = printableWMM * this.VISUAL_SCALE_FACTOR;
    const labelHeightPx = (this.parseToMM(settings.labelHeight) || 20) * this.VISUAL_SCALE_FACTOR;

    const rawPos = event.source.getFreeDragPosition();

    // Use the refined clamping
    const clampedPos = this.getClampedPosition(rawPos, labelWidthPx, labelHeightPx);

    // Update the position object
    if (Array.isArray(this.fieldPositions)) {
      this.fieldPositions = {};
    }
    this.fieldPositions[fieldKey] = clampedPos;

    // Reset the CDK internal transform perfectly
    event.source.setFreeDragPosition(clampedPos);
    this.saveAllUserSettings();
  }

  getBarcodeData(barcode: string) {
    this.stockService.getBarcodeDataWithBarcodeOrModelNo(barcode).subscribe((res: any) => {
      if (res.data && res.data.length > 0) {
        this.processFetchedItems(res.data);
      }
    });
  }

  renderBarcodes() {
    if (!this.barcodeCanvases || this.barcodeCanvases.length === 0) return;
    const size = this.barcodeSettingsForm.get('barcodeSize')?.value;
    const targetHeight = this.parseSizeToPx(size);
    this.barcodeCanvases.forEach((canvasRef, index) => {
      const tag = this.tags[index];
      if (canvasRef.nativeElement && tag?.barcode) {
        JsBarcode(canvasRef.nativeElement, tag.barcode, {
          format: 'CODE128', width: targetHeight / 20, height: targetHeight,
          fontSize: targetHeight / 4, displayValue: true, textAlign: 'center', margin: 0,
        });
      }
    });
  }

  parseToMM(value: string): number {
    if (!value) return 0;
    const val = String(value).toLowerCase().trim();
    if (val.endsWith('mm')) return parseFloat(val.replace('mm', ''));
    if (val.endsWith('px')) return parseFloat(val.replace('px', '')) * 0.264583;
    return parseFloat(val);
  }

  private parseSizeToPx(value: any): number {
    const MM_TO_PX = 96 / 25.4;
    const str = String(value).toLowerCase();
    if (str.endsWith('mm')) return parseFloat(str) * MM_TO_PX;
    if (str.endsWith('px')) return parseFloat(str);
    return parseFloat(str) || 40;
  }

  getLabel(key: string): string {
    return this.customLabels[key] || this.defaultFieldLabels[key] || key;
  }

  formatValue(tag: any, fieldKey: string): string {
    const val = tag[fieldKey];
    if (fieldKey === 'totalWeight') return `${val} g`;
    if (fieldKey === 'unitPrice') return `₹${val}`;
    return val;
  }

  private formatDimension(value: string | null, unit: string = 'px'): string | null {
    if (!value) return null;
    return isNaN(Number(value)) ? value : `${value}${unit}`;
  }

  updateCustomLabelsAndStyles(settings: any) {
    Object.keys(this.defaultFieldLabels).forEach((field) => {
      this.customLabels[field] = settings[field + 'Label'];
      const size = settings[field + 'Size'];
      const pxSize = size ? Math.max(8, Math.round(parseFloat(size) * 0.3528 * this.VISUAL_SCALE_FACTOR)) : null;
      this.fieldStyles[field] = { 'font-size': this.formatDimension(pxSize ? String(pxSize) : null, 'px') || 'inherit' };
    });
  }

  get currentTemplateType(): string {
    return this.barcodeSettingsForm.get('templateType')?.value || '';
  }

  onFieldDrop(event: CdkDragDrop<string[]>) {
    moveItemInArray(this.fieldKeys, event.previousIndex, event.currentIndex);
    // Removed saveAllUserSettings() from here
  }



  // Update visibility and save the row
  updateFieldVisibility(key: string, value: string): void {
    const isVisible = value.toLowerCase() === 'show';
    this.fieldVisibility = { ...this.fieldVisibility, [key]: isVisible };

    const type = this.currentTemplateType;
    if (!this.templateConfigs[type]) this.templateConfigs[type] = {};
    this.templateConfigs[type].visibility = { ...this.fieldVisibility };

    // Removed saveAllUserSettings() from here
  }

  onSubmitFieldSettings(): void {
    const settings = this.fieldSettingsForm.value;
    this.updateCustomLabelsAndStyles(settings);
    this.saveAllUserSettings(); // Button click triggers save
  }

  onSettingsSubmit() {
    this.applySettings();
    this.saveAllUserSettings(); // Button click triggers save
  }


  removeLabel(index: number) { this.tags.splice(index, 1); }

  initializeFieldSettings() {
    const initial: any = {};
    Object.keys(this.defaultFieldLabels).forEach(f => {
      initial[f + 'Label'] = this.defaultFieldLabels[f];
      initial[f + 'Size'] = '10';
    });
    this.fieldSettingsForm.patchValue(initial);
    this.updateCustomLabelsAndStyles(initial);
  }

  getTaggingList(firmId: number) {
    const params = new HttpParams()
      .set('std_product_type', 'piece-wise')
      .set('std_firm_id', firmId.toString())
      .set('stock_status', 'in');
    this.taggingService.getTaggings(params).subscribe({
      next: (res: any) => {
        if (res.data) this.taggingsList = res.data.map((i: any) => ({ barcode: i.std_barcode }));
      }
    });
  }

  onSelectOption(opt: string) {
    this.searchForm.patchValue({ searchInput: opt });
    this.showDropdown = false;
    this.onSearch();
  }

  handleFocus() {
    this.showDropdown = true;
    const val = this.searchForm.get('searchInput')?.value?.toLowerCase() || '';
    this.filteredOptions = this.taggingsList?.map((x: any) => x.barcode).filter((b: string) => b.toLowerCase().includes(val)) || [];
  }

  onBlurDropdown() { setTimeout(() => this.showDropdown = false, 150); }

  onKeydown(event: KeyboardEvent) {
    if (!this.filteredOptions.length) return;
    if (event.key === 'ArrowDown') this.selectedOptionIndex = (this.selectedOptionIndex + 1) % this.filteredOptions.length;
    else if (event.key === 'ArrowUp') this.selectedOptionIndex = (this.selectedOptionIndex - 1 + this.filteredOptions.length) % this.filteredOptions.length;
    else if (event.key === 'Enter' && this.selectedOptionIndex >= 0) this.onSelectOption(this.filteredOptions[this.selectedOptionIndex]);
  }

  printZplLabels() {
    const settings = { ...this.barcodeSettingsForm.getRawValue(), ...this.fieldSettingsForm.value };
    const resolvedLabels: any = {};
    this.fieldKeys.forEach(k => resolvedLabels[k] = this.getLabel(k));

    this.printManagerService.getSavedPrinters(this.firmId).subscribe({
      next: (res: any) => {
        const printers = res.data || [];
        const defaultPrinter = printers.find((p: any) => p.is_default);

        if (defaultPrinter && defaultPrinter.status === 'active') {
          // Normalize fields coordinate map
          const fieldsModel: any = {};
          this.fieldKeys.forEach(k => {
            fieldsModel[k] = {
              label: this.getLabel(k),
              fontSize: settings[k + 'Size'] || '10',
              visible: !!this.fieldVisibility[k],
              x: this.fieldPositions[k]?.x || 0,
              y: this.fieldPositions[k]?.y || 0
            };
          });

          // Build standard Label JSON contract
          const labelModel = {
            printer: {
              name: defaultPrinter.printer_name,
              language: defaultPrinter.printer_language,
              connection: defaultPrinter.printer_connection,
              dpi: defaultPrinter.printer_dpi || 203,
              address: defaultPrinter.printer_address || 'USB001',
              port: defaultPrinter.printer_port || '9100'
            },
            label: {
              width: this.parseToMM(settings.labelWidth),
              height: this.parseToMM(settings.labelHeight),
              dpi: parseInt(settings.printerDpi) || defaultPrinter.printer_dpi || 203,
              tailLength: settings.tailSide !== 'none' ? this.parseToMM(settings.tailLength) : 0,
              tailSide: settings.tailSide,
              barcodeSize: this.barcodeSettingsForm.get('barcodeSize')?.value,
              textAlign: settings.textAlign,
              topMargin: this.parseToMM(settings.topMargin) || 0,
              leftMargin: this.parseToMM(settings.leftMargin) || 0
            },
            fields: fieldsModel,
            tags: this.tags,
            copies: 1
          };

          this.notificationService.showSuccess('Sending print job to Hardware Gateway...', 'Info');
          this.printManagerService.printLabels(labelModel).subscribe({
            next: (printRes: any) => {
              this.notificationService.showSuccess('Label print job sent successfully!', 'Success');
              if (printRes && printRes.commands) {
                const firstTagCode = this.tags[0]?.code || 'tags';
                // if we want the prn file download please uncomment this line 
                // this.downloadPRNFile(printRes.commands, `tag_${firstTagCode}.prn`);
              }
            },
            error: (err) => {
              console.error('Local printer agent print failed:', err);
              this.notificationService.showError('Hardware Agent is offline or error occurred. Check agent status.', 'Error');
            }
          });
        } else {
          // Fallback to legacy Zebra Browser Print if no default printer configured in printer master
          this.notificationService.showSuccess('No default printer found. Using legacy Zebra Print fallback...', 'Info');
          this.zplService.printToLocalZebra(this.zplService.generateZPL(
            this.tags, this.fieldPositions, this.fieldVisibility, resolvedLabels, settings,
            (this.parseToMM(settings.labelWidth) - (settings.tailSide !== 'none' ? this.parseToMM(settings.tailLength) : 0)) * this.VISUAL_SCALE_FACTOR,
            this.parseToMM(settings.labelHeight) * this.VISUAL_SCALE_FACTOR, parseInt(settings.printerDpi)
          ));
        }
      },
      error: (err) => {
        console.error('Failed to query printer master config:', err);
        // Fallback on HTTP failure
        this.zplService.printToLocalZebra(this.zplService.generateZPL(
          this.tags, this.fieldPositions, this.fieldVisibility, resolvedLabels, settings,
          (this.parseToMM(settings.labelWidth) - (settings.tailSide !== 'none' ? this.parseToMM(settings.tailLength) : 0)) * this.VISUAL_SCALE_FACTOR,
          (this.parseToMM(settings.labelHeight) || 20) * this.VISUAL_SCALE_FACTOR, parseInt(settings.printerDpi)
        ));
      }
    });
  }

  // if we want the prn file download please uncomment this code this is the method to download the prn file
  // downloadPRNFile(content: string, filename: string) {
  //   const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
  //   const url = window.URL.createObjectURL(blob);
  //   const link = document.createElement('a');
  //   link.href = url;
  //   link.download = filename;
  //   link.click();
  //   window.URL.revokeObjectURL(url);
  // }

  getRemainingWidth(fieldKey: string): number {
    const settings = this.barcodeSettingsForm.getRawValue();

    const wMM = this.parseToMM(settings.labelWidth) || 40;
    const tailMM = settings.tailSide !== 'none' ? (this.parseToMM(settings.tailLength) || 0) : 0;
    const printableWMM = Math.max(10, wMM - tailMM);

    const labelWidthPx = printableWMM * this.VISUAL_SCALE_FACTOR;

    const currentX = this.fieldPositions[fieldKey]?.x || 0;

    // Subtracting a small buffer (5px) to prevent border touching
    const remaining = labelWidthPx - currentX - 5;
    return remaining > 0 ? remaining : 50; // Fallback to 50px
  }

  directPrint() {
    const contents = document.querySelector('.flex.flex-wrap.justify-center.gap-6.mt-8')?.innerHTML;
    const win = window.open('', '_blank', 'height=600,width=800');
    if (win && contents) {
      win.document.write(`<html><head><title>Print Preview</title><style>body{margin:0;padding:0;}.absolute{position:absolute;}.bg-white{background:#fff;}.relative{position:relative;}.no-print{display:none !important;}</style></head><body onload="window.print();window.close()">${contents}</body></html>`);
      win.document.close();
    }
  }

  focusNext(event: Event) {
    const target = (event as KeyboardEvent).target as HTMLElement;
    const form = target.closest('form');
    if (form) {
      const focusable = Array.from(form.querySelectorAll<HTMLElement>('input, select, textarea, button'))
        .filter(el => !el.hasAttribute('disabled') && el.tabIndex !== -1);
      const index = focusable.indexOf(target);
      if (index > -1 && index + 1 < focusable.length) focusable[index + 1].focus();
    }
  }

  openTemplateModal() {
    this.templateName = '';
    this.showTemplateModal = true;
  }

  closeTemplateModal() {
    this.showTemplateModal = false;
  }

  // Add this helper to generate slugs (e.g., "My Tag" -> "my-tag")
  private generateSlug(text: string): string {
    return text.toLowerCase().trim().replace(/ /g, '-').replace(/[^\w-]+/g, '');
  }

  saveTemplate() {
    if (!this.templateName.trim()) {
      this.notificationService.showError('Please enter a template name', 'Error');
      return;
    }

    const slug = this.generateSlug(this.templateName);

    // Check if slug already exists
    if (this.dbTemplates.find(t => t.slug === slug) || this.initialTemplateConfigs[slug]) {
      this.notificationService.showError('A template with this name already exists', 'Error');
      return;
    }

    // Take the CURRENT screen state to create the new template
    const payload = {
      firm_id: this.firmId,
      name: this.templateName,
      slug: slug,
      settings: this.barcodeSettingsForm.getRawValue(),
      positions: this.fieldPositions,
      font_sizes: this.fieldSettingsForm.getRawValue(), // Stores Labels + Sizes
      visibility: this.fieldVisibility
    };

    this.customizeService.saveBarcodeTemplate(payload).subscribe({
      next: (res: any) => {
        this.notificationService.showSuccess('New template created!', 'Success');

        // 1. Update local lists
        const newTemplate = res.data;
        this.dbTemplates.push(newTemplate);
        this.templateConfigs[slug] = {
          positions: newTemplate.positions,
          visibility: newTemplate.visibility,
          fieldSettings: newTemplate.font_sizes,
          settings: newTemplate.settings
        };

        // 2. Switch to the newly created template
        this.barcodeSettingsForm.patchValue({ templateType: slug }, { emitEvent: false });

        this.closeTemplateModal();
        this.onTemplateTypeChange(false); // Reload context
      },
      error: () => this.notificationService.showError('Failed to save template', 'Error')
    });
  }



  // 2. Updated initial configuration with defaults for all properties
  private initialTemplateConfigs: any = {
    '$25mmx25mm': {
      positions: { barcode: { x: 4, y: 7 }, itemCode: { x: 1, y: 62 }, itemName: { x: 1, y: 75 }, itemSize: { x: 75, y: 89 }, unitPrice: { x: 3, y: 36 }, totalWeight: { x: 3, y: 86 }, itemCategory: { x: 1, y: 47 }, itemStoneType: { x: 2, y: 45 }, itemColor: { x: 40, y: 83 } },
      visibility: { itemName: true, barcode: true, itemCode: true, totalWeight: true, unitPrice: true, itemCategory: true, itemSize: true, itemColor: false, itemStoneType: true },
      fieldSettings: { itemNameLabel: 'Product', itemNameSize: '5', barcodeLabel: 'Barcode', barcodeSize: '4', itemCodeLabel: 'Code', itemCodeSize: '5', totalWeightLabel: 'Weight', totalWeightSize: '5', unitPriceLabel: 'Price', unitPriceSize: '5', itemCategoryLabel: 'Category', itemCategorySize: '5', itemSizeLabel: 'Size', itemSizeSize: '5', itemColorLabel: 'Colour', itemColorSize: '5', itemStoneTypeLabel: 'Stone', itemStoneTypeSize: '5' }
    },
    '$40mmx20mm': {
      positions: { barcode: { x: 14, y: 8 }, itemCode: { x: 5, y: 51 }, itemName: { x: 4, y: 64 }, itemSize: { x: 6, y: 41 }, unitPrice: { x: 94, y: 50 }, totalWeight: { x: 95, y: 62 }, itemCategory: { x: 94, y: 39 }, itemStoneType: { x: 109, y: 60 }, itemColor: { x: 100, y: 60 } },
      visibility: { itemName: true, barcode: true, itemCode: true, totalWeight: true, unitPrice: true, itemCategory: true, itemSize: true, itemColor: false, itemStoneType: true },
      fieldSettings: { itemNameLabel: 'Product', itemNameSize: '5', barcodeLabel: 'Barcode', barcodeSize: '4', itemCodeLabel: 'Code', itemCodeSize: '5', totalWeightLabel: 'Weight', totalWeightSize: '5', unitPriceLabel: 'Price', unitPriceSize: '5', itemCategoryLabel: 'Category', itemCategorySize: '5', itemSizeLabel: 'Size', itemSizeSize: '5', itemColorLabel: 'Colour', itemColorSize: '5', itemStoneTypeLabel: 'Stone', itemStoneTypeSize: '5' }
    },
    '$50mmx25mm': {
      positions: { barcode: { x: 10, y: 12 }, itemCode: { x: 7, y: 80 }, itemName: { x: 8, y: 47 }, itemSize: { x: 8, y: 64 }, unitPrice: { x: 101, y: 76 }, totalWeight: { x: 97, y: 45 }, itemCategory: { x: 102, y: 60 }, itemStoneType: { x: 55, y: 60 }, itemColor: { x: 154, y: 83 } },
      visibility: { itemName: true, barcode: true, itemCode: true, totalWeight: true, unitPrice: true, itemCategory: true, itemSize: true, itemColor: false, itemStoneType: true },
      fieldSettings: { itemNameLabel: 'Product', itemNameSize: '7', barcodeLabel: 'Barcode', barcodeSize: '4', itemCodeLabel: 'Code', itemCodeSize: '7', totalWeightLabel: 'Weight', totalWeightSize: '7', unitPriceLabel: 'Price', unitPriceSize: '7', itemCategoryLabel: 'Category', itemCategorySize: '7', itemSizeLabel: 'Size', itemSizeSize: '7', itemColorLabel: 'Colour', itemColorSize: '7', itemStoneTypeLabel: 'Stone', itemStoneTypeSize: '7' }
    },
    '$100mmx15mm': {
      positions: { barcode: { x: 227, y: 26 }, itemCode: { x: 17, y: 5 }, itemName: { x: 20, y: 40 }, itemSize: { x: 19, y: 23 }, unitPrice: { x: 246, y: 6 }, totalWeight: { x: 127, y: 20 }, itemCategory: { x: 124, y: 4 }, itemStoneType: { x: 129, y: 39 }, itemColor: { x: 154, y: 40 } },
      visibility: { itemName: true, barcode: true, itemCode: true, totalWeight: true, unitPrice: true, itemCategory: true, itemSize: true, itemColor: false, itemStoneType: true },
      fieldSettings: { itemNameLabel: 'Product', itemNameSize: '7', barcodeLabel: 'Barcode', barcodeSize: '4', itemCodeLabel: 'Code', itemCodeSize: '7', totalWeightLabel: 'Weight', totalWeightSize: '7', unitPriceLabel: 'Price', unitPriceSize: '7', itemCategoryLabel: 'Category', itemCategorySize: '7', itemSizeLabel: 'Size', itemSizeSize: '7', itemColorLabel: 'Colour', itemColorSize: '7', itemStoneTypeLabel: 'Stone', itemStoneTypeSize: '7' }
    },
    '$custom': {
      positions: { barcode: { x: 227, y: 26 }, itemCode: { x: 17, y: 5 }, itemName: { x: 20, y: 40 }, itemSize: { x: 19, y: 23 }, unitPrice: { x: 246, y: 6 }, totalWeight: { x: 127, y: 20 }, itemCategory: { x: 124, y: 4 }, itemStoneType: { x: 129, y: 39 }, itemColor: { x: 154, y: 40 } },
      visibility: { itemName: true, barcode: true, itemCode: true, totalWeight: true, unitPrice: true, itemCategory: true, itemSize: true, itemColor: false, itemStoneType: true },
      fieldSettings: { itemNameLabel: 'Product', itemNameSize: '7', barcodeLabel: 'Barcode', barcodeSize: '4', itemCodeLabel: 'Code', itemCodeSize: '7', totalWeightLabel: 'Weight', totalWeightSize: '7', unitPriceLabel: 'Price', unitPriceSize: '7', itemCategoryLabel: 'Category', itemCategorySize: '7', itemSizeLabel: 'Size', itemSizeSize: '7', itemColorLabel: 'Colour', itemColorSize: '7', itemStoneTypeLabel: 'Stone', itemStoneTypeSize: '7' }
    }
  };

  openPrinterModal() {
    this.showPrinterModal = true;
    this.showAddPrinterForm = false;
    this.printerFormGroup.reset({
      printerLanguage: 'ZPL',
      printerConnection: 'USB',
      printerDpi: 203,
      printerPort: '9100',
      isDefault: false
    });
    this.loadSavedPrinters();
    this.discoverLocalPrinters();
  }

  closePrinterModal() {
    this.showPrinterModal = false;
  }

  loadSavedPrinters() {
    this.printManagerService.getSavedPrinters(this.firmId).subscribe({
      next: (res: any) => {
        this.savedPrinters = res.data || [];
      },
      error: () => {
        this.notificationService.showError('Failed to load saved printers', 'Error');
      }
    });
  }

  discoverLocalPrinters() {
    this.isDiscovering = true;
    this.discoveredPrinters = [];
    this.printManagerService.discoverLocalPrinters().subscribe({
      next: (res: any) => {
        this.discoveredPrinters = res.data || [];
        this.isDiscovering = false;
      },
      error: () => {
        this.isDiscovering = false;
        console.warn('Local print agent is not running or offline.');
      }
    });
  }

  savePrinterConfig() {
    if (this.printerFormGroup.invalid) {
      this.notificationService.showError('Please fill all required fields', 'Error');
      return;
    }

    const val = this.printerFormGroup.value;
    const payload = {
      firm_id: this.firmId,
      printer_name: val.printerName,
      printer_language: val.printerLanguage,
      printer_connection: val.printerConnection,
      printer_dpi: val.printerDpi,
      printer_address: val.printerAddress || 'USB001',
      printer_port: val.printerPort || '9100',
      is_default: !!val.isDefault,
      status: 'active'
    };

    this.printManagerService.savePrinter(payload).subscribe({
      next: () => {
        this.notificationService.showSuccess('Printer configuration saved successfully!', 'Success');
        this.showAddPrinterForm = false;
        this.loadSavedPrinters();
      },
      error: (err: any) => {
        this.notificationService.showError('Failed to save printer configuration: ' + (err.error?.message || err.message), 'Error');
      }
    });
  }

  deletePrinterConfig(id: number) {
    if (confirm('Are you sure you want to delete this printer configuration?')) {
      this.printManagerService.deletePrinter(id).subscribe({
        next: () => {
          this.notificationService.showSuccess('Printer deleted successfully', 'Success');
          this.loadSavedPrinters();
        },
        error: () => {
          this.notificationService.showError('Failed to delete printer', 'Error');
        }
      });
    }
  }

  makePrinterDefault(id: number) {
    this.printManagerService.setDefaultPrinter(id).subscribe({
      next: () => {
        this.notificationService.showSuccess('Default printer updated successfully', 'Success');
        this.loadSavedPrinters();
      },
      error: () => {
        this.notificationService.showError('Failed to update default printer', 'Error');
      }
    });
  }
}